import { Check, ExternalLink, Mic, Play, Trophy } from 'lucide-react';

import { estimateWorkoutMinutes } from '../../utils/workoutStats';
import { proPrice } from '../../utils/subscription';
import alexaPhoto from '../../../assets/landing/alexa-home-training.jpg';
import logo from '../../../assets/plates/nav-logo.png';

import { ACADEMY_VIDEOS, HERO_WORKOUT, formatLoad, useLanding } from './landingData';
import { ExerciseArt, Pill, Ring, SectionHead } from './parts';
import { totalSets } from './sessionState';

const asEntries = (workout) => workout.map((exercise) => ({ series: exercise.sets, interval: exercise.rest }));

/** One session in order: the day's workout, the how-to, the rest, the end with its record */
export const HomeMoments = () => {
  const { t, language, name } = useLanding();
  const m = (key, options) => t(`pages.Home.moments.${key}`, options);
  const s = (key, options) => t(`pages.WorkoutExecution.session.${key}`, options);
  const sets = totalSets(HERO_WORKOUT);
  const minutes = estimateWorkoutMinutes(asEntries(HERO_WORKOUT));
  const [bench, incline, fly] = HERO_WORKOUT;
  const record = { kg: 42.5, lbs: 100 };

  return (
    <section id="treinos" className="lp-sec" aria-labelledby="lp-moments-title">
      <div className="lp-wrap">
        <SectionHead id="lp-moments-title" title={m('title')} sub={m('sub')} />
        <ol className="lp-moments">
          <li className="lp-m">
            <span className="lp-m__node" aria-hidden="true" />
            <h3 className="lp-h3">{m('openTitle')}</h3>
            <p className="lp-m__body">{m('openBody')}</p>
            <div className="lp-m__ui" aria-hidden="true">
              <p className="lp-today__t">
                {t('pages.Home.demo.workout')} <Pill>{t('pages.Workouts.list.today')}</Pill>
              </p>
              <p className="lp-today__m">{t('pages.Home.demo.muscles')}</p>
              <p className="lp-today__x">{m('todayMeta', { exercises: HERO_WORKOUT.length, sets, minutes })}</p>
              <div className="lp-today__th">
                {HERO_WORKOUT.slice(0, 4).map((exercise) => (
                  <ExerciseArt key={exercise.id} file={exercise.file} />
                ))}
              </div>
              <span className="lp-startbtn">
                <Play aria-hidden="true" />
                {t('pages.Workouts.view.start')}
              </span>
            </div>
          </li>

          <li className="lp-m">
            <span className="lp-m__node" aria-hidden="true" />
            <h3 className="lp-h3">{m('howTitle')}</h3>
            <p className="lp-m__body">{m('howBody')}</p>
            <div className="lp-m__ui lp-how" aria-hidden="true">
              <ExerciseArt file={incline.file} className="lp-how__art" />
              <p className="lp-how__t">{name(incline)}</p>
              <ol className="lp-steps lp-steps--sm">
                {incline.steps[language].slice(0, 2).map((text) => (
                  <li key={text}>{text}</li>
                ))}
              </ol>
            </div>
          </li>

          <li className="lp-m">
            <span className="lp-m__node" aria-hidden="true" />
            <h3 className="lp-h3">{m('restTitle')}</h3>
            <p className="lp-m__body">{m('restBody')}</p>
            <div className="lp-m__ui lp-rest" aria-hidden="true">
              <Ring share={0.8} size={132} stroke={9} className="lp-rest__ring">
                <span className="lp-rest__t">
                  <b>1:12</b>
                  <span>{s('rest')}</span>
                </span>
              </Ring>
              <span className="lp-rest__adj">
                <span>{s('minus15Short')}</span>
                <span>{s('plus15Short')}</span>
              </span>
              <span className="lp-rest__next">
                <ExerciseArt file={fly.file} />
                <span>
                  <b>{name(fly)}</b>
                  <span>{s('restNextExercise', { dose: `${fly.sets} × ${fly.reps} · ${formatLoad(fly.load, language)}` })}</span>
                </span>
              </span>
            </div>
          </li>

          <li className="lp-m">
            <span className="lp-m__node lp-m__node--done" aria-hidden="true">
              <Check />
            </span>
            <h3 className="lp-h3">
              {m('doneTitle')} <Pill kind="pro">{t('pages.Home.build.pro')}</Pill>
            </h3>
            <p className="lp-m__body">{m('doneBody')}</p>
            <div className="lp-m__ui" aria-hidden="true">
              <p className="lp-done__t">
                {s('doneTitle')} <Pill kind="neutral">{t('pages.Home.demo.example')}</Pill>
              </p>
              <p className="lp-done__s">
                {t('pages.Home.demo.workout')} · {t('pages.Home.demo.muscles')}
              </p>
              <div className="lp-done__stats">
                <span>
                  <b>
                    38<small> {s('minutesUnit')}</small>
                  </b>
                  {s('statDuration')}
                </span>
                <span>
                  <b>{sets}</b>
                  {s('statSets', { count: sets })}
                </span>
                <span>
                  <b>{HERO_WORKOUT.length}</b>
                  {s('statExercises', { count: HERO_WORKOUT.length })}
                </span>
              </div>
              <div className="lp-rec">
                <p className="lp-rec__t">{s('recordsTitle', { count: 1 })}</p>
                <p className="lp-rec__r">
                  <Trophy />
                  <span>
                    <b>{name(bench)}</b>
                    {m('recordLine', { value: formatLoad(record, language), previous: formatLoad(bench.load, language) })}
                  </span>
                </p>
              </div>
            </div>
          </li>
        </ol>
      </div>
    </section>
  );
};

/** The Alexa band: the photo, the start phrase, and what to say during a workout (pt) or how to set it up (en) */
export const HomeAlexa = () => {
  const { t, language } = useLanding();
  const a = (key) => t(`pages.Home.alexa.${key}`);
  const ui = (key) => t(`pages.Tutorial.ui.${key}`);
  const isPt = language === 'pt';
  // The skill's commands are written down in Portuguese only (the Alexa page's voice guide); en has its setup steps
  const rows = isPt
    ? ['rest', 'end', 'resume'].map((key) => ({
        key,
        when: ui(`commands.${key}.when`),
        title: `“${ui(`commands.${key}.say`)}”`,
        text: ui(`commands.${key}.does`),
      }))
    : [
        { key: 's2', title: ui('s2Title'), text: ui('s2Text') },
        { key: 's3', title: ui('s3Title'), text: ui('s3Text') },
        { key: 's4', title: ui('s4Title'), text: `${ui('s4Text')} ${ui('s4Quote')}` },
      ];

  return (
    <section id="alexa" className="lp-band" aria-labelledby="lp-alexa-title">
      <div className="lp-wrap lp-voiceband">
        <img className="lp-voiceband__photo" src={alexaPhoto} alt={a('photoAlt')} width="1024" height="1024" loading="lazy" decoding="async" />
        <div className="lp-voiceband__text">
          <h2 id="lp-alexa-title" className="lp-phrase">
            {a('phrase')}
          </h2>
          <p className="lp-lead">{a('sub')}</p>
          <ul className="lp-cmds" aria-label={isPt ? ui('commandsTitle') : a('stepsLabel')}>
            {rows.map((row, index) => (
              <li key={row.key} className="lp-cmd">
                <span className={`lp-cmd__ic${isPt ? '' : ' lp-cmd__ic--step'}`} aria-hidden="true">
                  {isPt ? <Mic /> : index + 1}
                </span>
                <span>
                  {row.when && <small>{row.when}</small>}
                  <b>{row.title}</b>
                  <span>{row.text}</span>
                </span>
              </li>
            ))}
          </ul>
          <div className="lp-voiceband__act">
            <a className="lp-btn lp-btn--outline" href={t('alexaUrl')} target="_blank" rel="noreferrer">
              {a('cta')}
              <ExternalLink aria-hidden="true" />
              <span className="sr-only">{ui('newTab')}</span>
            </a>
            <p>{a('note')}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

/** The rest of Pro around the workouts: the Central de Aprendizado and the PDF sheet (the diet plan has its own section) */
export const HomeMore = () => {
  const { t, language, name } = useLanding();
  const m = (key) => t(`pages.Home.more.${key}`);
  const w = (key, options) => t(`pages.Workouts.view.${key}`, options);
  const sets = totalSets(HERO_WORKOUT);

  return (
    <section className="lp-sec" aria-labelledby="lp-more-title">
      <div className="lp-wrap">
        <SectionHead id="lp-more-title" title={m('title')} sub={m('sub')} />
        <div className="lp-more">
          <article className="lp-more__c">
            <div className="lp-more__text">
              <h3 className="lp-h3">{m('academyTitle')}</h3>
              <p className="lp-more__body">{m('academyBody')}</p>
            </div>
            <ul className="lp-more__ui lp-vids" aria-hidden="true">
              {ACADEMY_VIDEOS[language].map((video) => (
                <li key={video.title} className="lp-vid">
                  <span className="lp-vid__t">
                    <Play />
                  </span>
                  <span>
                    <b>{video.title}</b>
                    <span>{video.channel}</span>
                  </span>
                </li>
              ))}
            </ul>
          </article>

          <article className="lp-more__c">
            <div className="lp-more__text">
              <h3 className="lp-h3">{m('pdfTitle')}</h3>
              <p className="lp-more__body">{m('pdfBody')}</p>
            </div>
            <div className="lp-more__ui" aria-hidden="true">
              <div className="lp-paper">
                <p className="lp-paper__h">
                  {t('pages.Home.demo.workout')}
                  <img src={logo} alt="" width="20" height="20" loading="lazy" decoding="async" />
                </p>
                <p className="lp-paper__s">
                  {t('pages.Home.demo.muscles')} · {HERO_WORKOUT.length} {t('pages.WorkoutExecution.session.statExercises', { count: 6 })} ·{' '}
                  {sets} {t('pages.WorkoutExecution.session.statSets', { count: sets })}
                </p>
                <table>
                  <thead>
                    <tr>
                      <th>{w('colExercise')}</th>
                      <th>{w('colSetsReps')}</th>
                      <th>{w('colLoad')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {HERO_WORKOUT.slice(0, 3).map((exercise) => (
                      <tr key={exercise.id}>
                        <td>{name(exercise)}</td>
                        <td>{`${exercise.sets} × ${exercise.reps}`}</td>
                        <td>{formatLoad(exercise.load, language)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
};

/** Free beside Pro, read the same way: name, price, who it's for, what's in it, one button */
export const HomePlans = ({ onSignup }) => {
  const { t, language } = useLanding();
  const p = (key) => t(`pages.Home.plans.${key}`);
  const freeFeatures = ['free1', 'free2', 'free3', 'free4', 'free5'];
  const proFeatures = ['pro1', 'pro2', 'pro3', 'pro4', 'pro5', 'pro6', 'pro7'];

  return (
    <section id="planos" className="lp-sec" aria-labelledby="lp-plans-title">
      <div className="lp-wrap">
        <SectionHead id="lp-plans-title" title={p('title')} center />
        <div className="lp-plans">
          <article className="lp-plan">
            <h3 className="lp-plan__name">{p('freeName')}</h3>
            <p className="lp-plan__desc">{t('pages.Plans.freePlanDesc')}</p>
            <ul className="lp-plan__list">
              {freeFeatures.map((key) => (
                <li key={key}>
                  <Check aria-hidden="true" />
                  {p(key)}
                </li>
              ))}
            </ul>
            <button type="button" className="lp-btn lp-btn--lg lp-btn--outline" onClick={onSignup}>
              {p('freeCta')}
            </button>
          </article>
          <article className="lp-plan lp-plan--pro">
            <h3 className="lp-plan__name">{t('pages.Plans.proPlan')}</h3>
            <p className="lp-plan__price">
              {proPrice(language)}
              <small>{t('pages.Plans.perMonth')}</small>
            </p>
            <p className="lp-plan__desc">{p('proDesc')}</p>
            <ul className="lp-plan__list">
              {proFeatures.map((key) => (
                <li key={key}>
                  <Check aria-hidden="true" />
                  {p(key)}
                </li>
              ))}
            </ul>
            <button type="button" className="lp-btn lp-btn--lg lp-btn--go" onClick={onSignup}>
              {t('pages.Plans.getPro')}
            </button>
          </article>
        </div>
      </div>
    </section>
  );
};
